// ChatInterface.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

// Import your components (assuming they are in the same directory or adjust path)
import ChatSidebar from "./ChatSidebar";
import ChatHeader from "./ChatHeader";
import ChatBody from "./ChatBody";
import MessageInput from "./MessageInput";
import fetchApi from "../../../../../Lib/api";
import useUserStore from "../../../../../../stores/userStore";
import consumer from "../../../../../../channels/consumer";
import { Loader } from "@mantine/core";
import GroupManagementModal from "./GroupManagementModal";

// Context Menu Component (from your code, dark classes removed)
const MessageContextMenu = ({
  visible,
  x,
  y,
  messageId,
  pinnedMessageId,
  onReply,
  onCopy,
  onPin,
  onSelect,
  onDelete,
}) => {
  if (!visible) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: `${y}px`,
        left: `${x}px`,
        zIndex: 100,
      }}
      className="w-52 mt-1 rounded-md bg-white shadow-lg border border-gray-200 focus:outline-none animate-fade-in-fast"
      role="menu"
      aria-orientation="vertical"
      tabIndex="-1"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="py-1" role="none">
        <button
          onClick={() => onReply(messageId)}
          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4 mr-3 text-gray-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
            ></path>
          </svg>
          Reply
        </button>

        <button
          onClick={() => onCopy(messageId)}
          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4 mr-3 text-gray-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z"
            ></path>
          </svg>
          Copy
        </button>

        <button
          onClick={() => onPin(messageId)}
          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4 mr-3 text-gray-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 3.75V16.5L12 14.25 7.5 16.5V3.75m9 0H18A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6A2.25 2.25 0 016 3.75h1.5m9 0h-9"
            ></path>
          </svg>
          {pinnedMessageId === messageId ? "Unpin" : "Pin"}
        </button>

        <button
          onClick={() => onSelect(messageId)}
          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4 mr-3 text-gray-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          Select
        </button>

        <div className="my-1 border-t border-gray-100"></div>

        <button
          onClick={() => onDelete(messageId)}
          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-red-600 hover:bg-red-50 focus:bg-red-50"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4 mr-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            ></path>
          </svg>
          Delete Message
        </button>
      </div>
    </div>
  );
};

// Empty Conversation State Component (from your code, dark classes removed)
const EmptyConversationState = ({
  onStartNewConversation, // Handler to start a new conversation (passed from ChatInterface)
  showContacts, // boolean: Whether to show the contact list in this empty state view
  setShowContacts,
  contacts, // array: The list of available contacts (users) to display
  onCreateGroupConversation,
  filter,
  setFilter,
  // Add missing props for search functionality
  composeSearchTerm,
  onComposeSearchChange,
  isLoadingModalSearch,
  modalSearchError,
  typingUsers
}) => (
  // The main container layout mimicking the ChatInterface structure
  <div className="flex h-[85vh] w-full overflow-hidden font-sans antialiased text-gray-900 bg-gray-100">
    {/* Sidebar Area - Render ChatSidebar, potentially showing contacts */}
    <div className="w-full max-w-xs flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
      <ChatSidebar
        // Pass minimal conversation data since it's an empty state
        conversations={{}}
        activeConversationId={null}
        // Pass dummy or no-op handlers for conversation selection/actions
        onSelectConversation={() => {}}
        onArchiveConversation={() => {}}
        onDeleteConversationRequest={() => {}}
        // Pass the handler for starting a new conversation
        onStartNewConversation={onStartNewConversation}
        // Pass the handler for creating a group conversation if needed in this view
        onCreateGroupConversation={onCreateGroupConversation} // Pass if ChatSidebar's compose modal is used here
        contacts={contacts}
        composeSearchTerm={composeSearchTerm} // <--- Add missing search term state
        onComposeSearchChange={onComposeSearchChange} // <--- Add missing search change handler
        isLoadingModalSearch={isLoadingModalSearch} // <--- Add missing loading state
        modalSearchError={modalSearchError} // <--- Add missing error state
        typingUsers={typingUsers} // <--- Add missing typing users
        showContacts={showContacts} // ChatSidebar's internal compose modal manages visibility
        setShowContacts={setShowContacts}
        filter={filter}
        setFilter={setFilter}
      />
    </div>

    {/* Main Empty State Message Area */}
    <div className="flex-grow flex flex-col items-center justify-center bg-white dark:bg-gray-900">
      <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
        No conversations here.
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-md">
        Start a new chat to begin messaging!
      </p>
        <button
          onClick={() => setShowContacts((p) => !p)}
          className="mt-6 px-6 py-3 bg-indigo-500 text-white rounded-md text-lg font-semibold hover:bg-indigo-600 transition duration-200"
        >
          Start New Chat
        </button>

    </div>
  </div>
);

// Loading State Component (from your code, dark classes removed)
const LoadingState = () => (
  <Loader className="mx-auto mt-10" color="indigo" size="lg" variant="dots" />
);

// Main Chat Interface Component
const ChatInterface = () => {
  // Get the current user's ID from the user store
  const {
    user: { user },
  } = useUserStore((state) => ({ user: state.user })); // Adjust state access as per your store structure
  const currentUserId = user?.id; // Use optional chaining in case user object is null initially
  const isUserReady = !!currentUserId; // Flag to indicate if current user data is loaded

  // --- Core state ---
  const [conversations, setConversations] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null); // No active convo initially
  const [showSidebarOnMobile, setShowSidebarOnMobile] = useState(true); // Track sidebar visibility on mobile
  const [typingUsers, setTypingUsers] = useState({});
  const [composeSearchTerm, setComposeSearchTerm] = useState("");
  const [isLoadingModalSearch, setIsLoadingModalSearch] = useState(false);
  const [modalSearchError, setModalSearchError] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const [messageSearchTerm, setMessageSearchTerm] = useState('')
  const [filter, setFilter] = useState(
    new URLSearchParams(window.location.hash.split('?')[1])?.get('slack_connect') === 'true' ? 'slack' : 'nowcollab'
  )

  // State for managing the message being replied to
  const [replyingToMessage, setReplyingToMessage] = useState(null);

  // --- Loading and Error States ---
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [conversationsLoadError, setConversationsLoadError] = useState(null); // Error for fetching conversations

  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesLoadError, setMessagesLoadError] = useState(null); // Error for fetching active conversation messages

  const [allUsers, setAllUsers] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersLoadError, setUsersLoadError] = useState(null);

  const [showGroupManagementModal, setShowGroupManagementModal] = useState(false);

  // --- UI State ---
  // Selection mode state
  const [selectModeActive, setSelectModeActive] = useState(false);

  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    messageId: null,
  });

  // Refs for DOM elements
  const chatBodyRef = useRef(null);
  // Ref to store the Action Cable subscription object
  const conversationSubscriptionRef = useRef(null); // Conversation-specific channel
  const userSubscriptionRef = useRef(null); // User-specific channel
  const presenceSubscriptionRef = useRef(null);
  const typingTimeoutsRef = useRef({});

  // --- API and Action Cable Data Fetching and Management ---

const handleComposeSearchChange = useCallback((event) => {
  setComposeSearchTerm(event.target.value);
}, []);

  useEffect(() => {
    const searchTerm = composeSearchTerm.trim();

    const debounceTimer = setTimeout(() => {
      setIsLoadingModalSearch(true);
      setModalSearchError(null);

      const url = searchTerm
        ? `/conversations/search_users?search=${encodeURIComponent(searchTerm)}`
        : `/conversations/search_users`;

      fetchApi(url, "GET")
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((backendContacts) => {
          setAllUsers(backendContacts);
        })
        .catch((error) => {
          console.error("Modal search fetch error:", error);
          setModalSearchError("Failed to load contacts.");
          setAllUsers([]);
        })
        .finally(() => {
          setIsLoadingModalSearch(false);
          setIsLoadingUsers(false);
        });
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [composeSearchTerm, fetchApi, setAllUsers]);

  useEffect(() => {
    if (!isUserReady || isLoadingUsers) return;

    const fetchConversations = async () => {
      setIsLoadingConversations(true);
      setConversationsLoadError(null);
      try {
        const response = await fetchApi(`/conversations?filter=${filter}`, "GET")
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Transform the array response into an object keyed by conversation ID for easier lookup
        const conversationsObject = data.reduce((acc, convo) => {
          // Start with the backend list item data
          acc[convo.id] = {
            ...convo,
            messages: [], // Messages are not in the list view
            pinnedMessageId: null, // Pinned message ID is not in the list view
          };
          return acc;
        }, {});
        setConversations(conversationsObject);

        const hash = window.location.hash;
        let urlConversationId = null;

        if (hash.includes("/messages")) {
          const queryStringAfterHash = hash.split("/messages")[1];
          if (queryStringAfterHash) {
            const params = new URLSearchParams(
              queryStringAfterHash.substring(1)
            );
            urlConversationId = params.get("conversationId");
          }
        }

        let targetConversationId = null;

        if (urlConversationId && conversationsObject[urlConversationId]) {
          targetConversationId = urlConversationId;
          console.log(
            `Setting active conversation from URL: ${urlConversationId}`
          );
        } else {
          const firstNonArchivedConvoId =
            Object.entries(conversationsObject).find(
              ([id, convo]) => !convo.isArchived
            )?.[0] || null;
          targetConversationId = firstNonArchivedConvoId;
          console.log(
            `Setting active conversation to first non-archived: ${targetConversationId}`
          );
        }

        setActiveConversationId(targetConversationId);

        if (urlConversationId) {
          const baseUrl = window.location.href.split("?")[0];
          const hashIndex = baseUrl.indexOf("#");
          if (hashIndex > -1) {
            const hashPart = baseUrl.substring(hashIndex);
            const cleanedUrl =
              baseUrl.substring(0, hashIndex) + hashPart.split("?")[0];
            window.history.replaceState({}, document.title, cleanedUrl);
            console.log(`Cleaned URL: ${cleanedUrl}`);
          }
        }
      } catch (error) {
        console.error("Error fetching conversations:", error);
        setConversationsLoadError("Failed to load conversations."); // Set error state
        // TODO: Implement user-facing error message display
      } finally {
        setIsLoadingConversations(false);
      }
    };

    fetchConversations(); // Perform the initial fetch
  }, [isUserReady, isLoadingUsers, filter]);

  const handleActionCableMessage = useCallback(
    (data) => {
      const {
        type,
        conversation_id,
        user_id,
        message,
        attachments,
        conversation,
        message_id,
        status,
        is_typing,
        last_seen_at,
        attachment_id,
      } = data;

      if (type === "presence" && user_id && status) {
        setAllUsers((prevUsers) => {
          if (prevUsers[user_id]) {
            return {
              ...prevUsers,
              [user_id]: {
                ...prevUsers[user_id],
                status: status,
                lastSeen:
                  status === "online"
                    ? "online"
                    : last_seen_at || prevUsers[user_id].lastSeen,
              },
            };
          }
          return prevUsers;
        });
        return;
      }

      if (type === "new_conversation" && conversation?.id) {
        setConversations((prev) => {
          if (!prev[conversation.id]) {
            return { ...prev, [conversation.id]: conversation };
          }
          return prev;
        });
        return;
      }

      if (type === "error" && data.message) {
        console.error(
          `Action Cable: Received error broadcast: ${data.message}`,
          data
        );
        return;
      }

      const targetConvoId =
        conversation_id || message?.conversation_id || conversation?.id;

      if (!targetConvoId) {
        console.warn(
          "Received broadcast without a target conversation ID:",
          data
        );
        return;
      }

      setConversations((prev) => {
        const currentConvo = prev[targetConvoId];

        if (!currentConvo && type !== "new_conversation") {
          console.warn(
            `Received broadcast for unknown conversation ID ${targetConvoId}, ignoring type: ${type}`,
            data
          );
          return prev;
        }

        let updatedConvo = currentConvo
          ? {
              ...currentConvo,
              messages: currentConvo.messages ? [...currentConvo.messages] : [],
            }
          : { id: targetConvoId, messages: [] };

        switch (type) {
          case "new_message":
            const newMessage = data.message;
            if (!newMessage) {
              console.warn(
                "Received new_message broadcast without message payload:",
                data
              );
              return prev;
            }

            const messageAlreadyExists = updatedConvo.messages?.some(
              (msg) => String(msg.id) === String(newMessage.id)
            );

            if (!messageAlreadyExists) {
              let messagesArray = updatedConvo.messages
                ? [...updatedConvo.messages]
                : [];

              const tempIndex = messagesArray.findIndex(
                (msg) => msg.id && String(msg.id).startsWith("temp-")
              );

              if (tempIndex !== -1) {
                messagesArray[tempIndex] = newMessage;
                if (messagesArray[tempIndex].attachments) {
                  messagesArray[tempIndex].attachments.forEach((att) => {
                    if (att.url && att.url.startsWith("blob:"))
                      URL.revokeObjectURL(att.url);
                    if (
                      att.thumbnailUrl &&
                      att.thumbnailUrl.startsWith("blob:")
                    )
                      URL.revokeObjectURL(att.thumbnailUrl);
                  });
                }
              } else {
                messagesArray.push(newMessage);
              }
              updatedConvo.messages = messagesArray;

              const lastMeaningfulMsgIndex = messagesArray.findLastIndex(
                (msg) => msg.type !== "date"
              );

              if (lastMeaningfulMsgIndex !== -1) {
                const lastMsg = messagesArray[lastMeaningfulMsgIndex];
                updatedConvo.lastPreview =
                  lastMsg.text?.substring(0, 50) ||
                  lastMsg.attachments?.[0]?.name ||
                  (lastMsg.attachments?.length > 0
                    ? `Attachment${lastMsg.attachments.length > 1 ? "s" : ""}`
                    : null) ||
                  (lastMsg.audioUrl ? "Audio message" : null) ||
                  "Message";
                updatedConvo.lastTime = lastMsg.timestamp;
                updatedConvo.isRecordingPreview = !!lastMsg.audioUrl;
              }
              updatedConvo.isArchived = false;
            } else {
              console.log(
                `Message ${newMessage.id} already exists in conversation ${targetConvoId}, ignoring new_message broadcast.`
              );
            }

            break;

          case "message_updated":
            const updatedMessage = data.message;
            if (!updatedMessage || !updatedConvo.messages) {
              console.warn(
                "Received message_updated broadcast without message payload or messages array:",
                data
              );
              return prev;
            }
            updatedConvo.messages = updatedConvo.messages.map((msg) =>
              String(msg.id) === String(updatedMessage.id)
                ? updatedMessage
                : msg
            );

            const lastMeaningfulMsgIndexAfterUpdate =
              updatedConvo.messages.findLastIndex((msg) => msg.type !== "date");
            if (
              lastMeaningfulMsgIndexAfterUpdate !== -1 &&
              String(
                updatedConvo.messages[lastMeaningfulMsgIndexAfterUpdate].id
              ) === String(updatedMessage.id)
            ) {
              const lastMsg =
                updatedConvo.messages[lastMeaningfulMsgIndexAfterUpdate];
              updatedConvo.lastPreview =
                lastMsg.text?.substring(0, 50) ||
                lastMsg.attachments?.[0]?.name ||
                (lastMsg.attachments?.length > 0
                  ? `Attachment${lastMsg.attachments.length > 1 ? "s" : ""}`
                  : null) ||
                (lastMsg.audioUrl ? "Audio message" : null) ||
                "Message";
              updatedConvo.lastTime = lastMsg.timestamp;
              updatedConvo.isRecordingPreview = !!lastMsg.audioUrl;
            }
            break;

          case "delete_message":
            const deletedMessageId = data.message_id;
            if (!deletedMessageId || !updatedConvo.messages) {
              console.warn(
                "Received delete_message broadcast without message_id or messages array:",
                data
              );
              return prev;
            }
            updatedConvo.messages = updatedConvo.messages.filter(
              (msg) => String(msg.id) !== String(deletedMessageId)
            );

            if (
              String(updatedConvo.pinnedMessageId) === String(deletedMessageId)
            ) {
              updatedConvo.pinnedMessageId = null;
            }

            if (updatedConvo.messages.length > 0) {
              const lastMeaningfulMsgIndex =
                updatedConvo.messages.findLastIndex(
                  (msg) => msg.type !== "date"
                );

              if (lastMeaningfulMsgIndex !== -1) {
                const lastMsg = updatedConvo.messages[lastMeaningfulMsgIndex];
                updatedConvo.lastPreview =
                  lastMsg.text?.substring(0, 50) ||
                  lastMsg.attachments?.[0]?.name ||
                  (lastMsg.attachments?.length > 0
                    ? `Attachment${lastMsg.attachments.length > 1 ? "s" : ""}`
                    : null) ||
                  (lastMsg.audioUrl ? "Audio message" : null) ||
                  "Message";
                updatedConvo.lastTime = lastMsg.timestamp;
                updatedConvo.isRecordingPreview = !!lastMsg.audioUrl;
              } else {
                updatedConvo.lastPreview = "Chat empty";
                updatedConvo.lastTime = "";
                updatedConvo.isRecordingPreview = false;
              }
            } else {
              updatedConvo.lastPreview = "Chat empty";
              updatedConvo.lastTime = "";
              updatedConvo.isRecordingPreview = false;
            }

            if (
              replyingToMessage &&
              String(replyingToMessage.id) === String(deletedMessageId)
            ) {
              setReplyingToMessage(null);
            }
            if (
              selectedMessageIds.has(deletedMessageId) ||
              selectedMessageIds.has(String(deletedMessageId))
            ) {
              setSelectedMessageIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(deletedMessageId);
                newSet.delete(String(deletedMessageId));
                if (newSet.size === 0) setSelectModeActive(false);
                return newSet;
              });
            }
            break;

          case "new_attachments":
            const messageIdForAttachments = data.message_id;
            const newAttachments = data.attachments;
            if (
              !messageIdForAttachments ||
              !newAttachments ||
              newAttachments.length === 0 ||
              !updatedConvo.messages
            ) {
              console.warn(
                "Received new_attachments broadcast without message_id, attachments, or messages array:",
                data
              );
              return prev;
            }
            updatedConvo.messages = updatedConvo.messages.map((msg) => {
              if (String(msg.id) === String(messageIdForAttachments)) {
                const existingAttachmentIds = new Set(
                  msg.attachments?.map((att) => att.id)
                );
                const attachmentsToAdd = newAttachments.filter(
                  (att) => !existingAttachmentIds.has(att.id)
                );

                const messageWithoutTempProps = { ...msg };
                delete messageWithoutTempProps.isUploading;
                delete messageWithoutTempProps.isSending;
                delete messageWithoutTempProps.fileData;

                return {
                  ...messageWithoutTempProps,
                  attachments: [
                    ...(msg.attachments || []),
                    ...attachmentsToAdd,
                  ],
                  readReceipt:
                    messageWithoutTempProps.readReceipt === "sending"
                      ? "sent"
                      : messageWithoutTempProps.readReceipt,
                };
              }
              return msg;
            });

            const lastMeaningfulMsgIndexAfterAttachments =
              updatedConvo.messages.findLastIndex((msg) => msg.type !== "date");
            if (
              lastMeaningfulMsgIndexAfterAttachments !== -1 &&
              String(
                updatedConvo.messages[lastMeaningfulMsgIndexAfterAttachments].id
              ) === String(messageIdForAttachments)
            ) {
              const updatedMsg =
                updatedConvo.messages[lastMeaningfulMsgIndexAfterAttachments];
              updatedConvo.lastPreview =
                updatedMsg.caption?.substring(0, 50) ||
                updatedMsg.attachments?.[0]?.name ||
                (updatedMsg.attachments?.length > 0
                  ? `Attachment${updatedMsg.attachments.length > 1 ? "s" : ""}`
                  : null) ||
                "Message";
              updatedConvo.isRecordingPreview = !!updatedMsg.audioUrl;
            }
            break;

          case "delete_attachment":
            const messageIdWithDeletedAttachment = data.message_id;
            const deletedAttachmentId = data.attachment_id;
            if (
              !messageIdWithDeletedAttachment ||
              !deletedAttachmentId ||
              !updatedConvo.messages
            ) {
              console.warn(
                "Received delete_attachment broadcast without message_id, attachment_id, or messages array:",
                data
              );
              return prev;
            }
            updatedConvo.messages = updatedConvo.messages.map((msg) => {
              if (
                String(msg.id) === String(messageIdWithDeletedAttachment) &&
                msg.attachments
              ) {
                const attachmentsAfterFiltering = msg.attachments.filter(
                  (att) => String(att.id) !== String(deletedAttachmentId)
                );
                return {
                  ...msg,
                  attachments: attachmentsAfterFiltering,
                };
              }
              return msg;
            });

            const lastMeaningfulMsgIndexAfterAttachmentDelete =
              updatedConvo.messages.findLastIndex((msg) => msg.type !== "date");
            if (
              lastMeaningfulMsgIndexAfterAttachmentDelete !== -1 &&
              String(
                updatedConvo.messages[
                  lastMeaningfulMsgIndexAfterAttachmentDelete
                ].id
              ) === String(messageIdWithDeletedAttachment)
            ) {
              const updatedMsg =
                updatedConvo.messages[
                  lastMeaningfulMsgIndexAfterAttachmentDelete
                ];
              updatedConvo.lastPreview =
                updatedMsg.text?.substring(0, 50) ||
                updatedMsg.attachments?.[0]?.name ||
                (updatedMsg.attachments?.length > 0
                  ? `Attachment${updatedMsg.attachments.length > 1 ? "s" : ""}`
                  : null) ||
                (updatedMsg.audioUrl ? "Audio message" : null) ||
                "Message";
              updatedConvo.isRecordingPreview = !!updatedMsg.audioUrl;
            }
            break;

          case "user_typing_status":
            if (
              !conversation_id ||
              !user_id ||
              user_id === currentUserId ||
              is_typing === undefined
            ) {
              return prev;
            }

            setTypingUsers((prevTyping) => {
              const updatedTyping = { ...prevTyping };

              if (!updatedTyping[conversation_id]) {
                updatedTyping[conversation_id] = {};
              }

              const userTimeoutKey = `${conversation_id}-${user_id}`;

              if (is_typing) {
                updatedTyping[conversation_id][user_id] = true;

                if (typingTimeoutsRef.current[userTimeoutKey]) {
                  clearTimeout(typingTimeoutsRef.current[userTimeoutKey]);
                }

                const TYPING_INDICATOR_DELAY = 2000;
                const timeoutId = setTimeout(() => {
                  setTypingUsers((innerPrevTyping) => {
                    const innerUpdatedTyping = { ...innerPrevTyping };
                    if (innerUpdatedTyping[conversation_id]?.[user_id]) {
                      delete innerUpdatedTyping[conversation_id][user_id];
                      if (
                        Object.keys(innerUpdatedTyping[conversation_id])
                          .length === 0
                      ) {
                        delete innerUpdatedTyping[conversation_id];
                      }
                      return innerUpdatedTyping;
                    }
                    return innerPrevTyping;
                  });
                  delete typingTimeoutsRef.current[userTimeoutKey];
                }, TYPING_INDICATOR_DELAY);

                typingTimeoutsRef.current[userTimeoutKey] = timeoutId;
              } else {
                if (updatedTyping[conversation_id]?.[user_id]) {
                  delete updatedTyping[conversation_id][user_id];
                  if (
                    Object.keys(updatedTyping[conversation_id]).length === 0
                  ) {
                    delete updatedTyping[conversation_id];
                  }
                }

                if (typingTimeoutsRef.current[userTimeoutKey]) {
                  clearTimeout(typingTimeoutsRef.current[userTimeoutKey]);
                  delete typingTimeoutsRef.current[userTimeoutKey];
                }
              }

              return updatedTyping;
            });
            return prev;

          case "user_presence":
            if (!user_id || !status || !updatedConvo.participants) {
              console.warn(
                "Received user_presence broadcast without user_id, status, or participants array:",
                data
              );
              return prev;
            }
            updatedConvo.participants = updatedConvo.participants.map((p) => {
              if (String(p.id) === String(user_id)) {
                return {
                  ...p,
                  lastSeen:
                    status === "online" ? "online" : last_seen_at || p.lastSeen,
                };
              }
              return p;
            });
            break;

          case "read_receipt_updated":
            const readReceiptUserId = data.user_id;
            const lastReadMessageIdFromBroadcast = data.last_read_message_id;
            if (
              !lastReadMessageIdFromBroadcast ||
              !readReceiptUserId ||
              !updatedConvo.participants
            ) {
              console.warn(
                "Received read_receipt_updated broadcast without last_read_message_id, user_id, or participants array:",
                data
              );
              return prev;
            }
            updatedConvo.participants = updatedConvo.participants.map((p) => {
              if (String(p.id) === String(readReceiptUserId)) {
                const currentLastReadId = p.lastReadMessageId || 0;
                if (lastReadMessageIdFromBroadcast > currentLastReadId) {
                  return {
                    ...p,
                    lastReadMessageId: lastReadMessageIdFromBroadcast,
                  };
                }
              }
              return p;
            });
            break;

          case "conversation_updated":
            const updatedConvoData = data.conversation;
            if (!updatedConvoData?.id) {
              console.warn(
                "Received conversation_updated broadcast without conversation payload or ID:",
                data
              );
              return prev;
            }
            if (prev[updatedConvoData.id]) {
              updatedConvo = {
                ...prev[updatedConvoData.id],
                ...updatedConvoData,
                messages: prev[updatedConvoData.id].messages,
              };
              updatedConvo.pinnedMessageId = updatedConvoData.pinnedMessageId;
              updatedConvo.isArchived = updatedConvoData.isArchived;
              if (updatedConvo.type === "group")
                updatedConvo.title = updatedConvoData.title;
              updatedConvo.avatar = updatedConvoData.avatar;
            } else {
              console.warn(
                `Received conversation_updated for unknown conversation ID ${updatedConvoData.id}.`
              );
              return prev;
            }
            break;

          case "participant_added":
            const newParticipant = data.participant;
            if (!newParticipant?.id || !updatedConvo.participants) {
              console.warn(
                "Received participant_added broadcast without participant payload or participants array:",
                data
              );
              return prev;
            }
            if (
              !updatedConvo.participants.some(
                (p) => String(p.id) === String(newParticipant.id)
              )
            ) {
              updatedConvo.participants = [
                ...updatedConvo.participants,
                newParticipant,
              ];
            } else {
              console.log(
                `Participant ${newParticipant.id} already in conversation ${targetConvoId}, ignoring participant_added broadcast.`
              );
            }
            break;

          case "participant_removed":
            const removedParticipantId = data.participant_id;
            if (!removedParticipantId || !updatedConvo.participants) {
              console.warn(
                "Received participant_removed broadcast without participant_id or participants array:",
                data
              );
              return prev;
            }
            const participantsAfterRemoval = updatedConvo.participants.filter(
              (p) => String(p.id) !== String(removedParticipantId)
            );
            if (
              participantsAfterRemoval.length < updatedConvo.participants.length
            ) {
              updatedConvo.participants = participantsAfterRemoval;
            } else {
              console.log(
                `Participant ${removedParticipantId} not found in conversation ${targetConvoId}, ignoring participant_removed broadcast.`
              );
            }
            break;

          case "confirm_subscription":
          case "ping":
            return prev;

          default:
            console.warn(
              "Received unhandled Action Cable broadcast type:",
              type,
              data
            );
            return prev;
        }

        return {
          ...prev,
          [targetConvoId]: updatedConvo,
        };
      });
    },
    [
      activeConversationId,
      replyingToMessage,
      selectedMessageIds,
      currentUserId,
      setConversations,
      setAllUsers,
      setTypingUsers,
      setReplyingToMessage,
      setSelectedMessageIds,
      setSelectModeActive,
      chatBodyRef,
      typingTimeoutsRef,
    ]
  );

  useEffect(() => {
    const clearAllTypingTimeouts = () => {
      for (const timeoutKey in typingTimeoutsRef.current) {
        clearTimeout(typingTimeoutsRef.current[timeoutKey]);
      }
      typingTimeoutsRef.current = {};
    };

    clearAllTypingTimeouts();

    return () => {
      clearAllTypingTimeouts();
    };
  }, [activeConversationId, typingTimeoutsRef]);

  // Effect to fetch messages for the active conversation and manage Action Cable subscription
  useEffect(() => {
    const unsubscribeFromPrevious = () => {
      if (conversationSubscriptionRef.current) {
        console.log(
          `Unsubscribing from conversation ${conversationSubscriptionRef.current.identifier}`
        );
        conversationSubscriptionRef.current.unsubscribe();
        conversationSubscriptionRef.current = null; // Clear the ref
      }
    };

    if (!isUserReady || !activeConversationId || isLoadingUsers) {
      unsubscribeFromPrevious(); // Ensure we unsubscribe if conditions are no longer met
      setConversations((prev) => {
        if (prev[activeConversationId]) {
          return {
            ...prev,
            [activeConversationId]: {
              ...prev[activeConversationId],
              messages: [],
            },
          };
        }
        return prev;
      });
      return; 
    }

    // fetchAndSubscribeToConversation function: Fetches detailed conversation data and subscribes to Action Cable
    const fetchAndSubscribeToConversation = async (convoId) => {
      setIsLoadingMessages(true); // Set loading state for messages
      setMessagesLoadError(null); // Clear previous errors for messages
      try {
        // Fetch the detailed conversation data from the backend
        let url = `/conversations/${convoId}`;
        if (messageSearchTerm) {
          url += `?message_search_term=${encodeURIComponent(messageSearchTerm)}`;
        }

        const response = await fetchApi(url, "GET"); 
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json(); 

        setConversations((prev) => ({
          ...prev,
          [convoId]: data,
        }));

        setIsLoadingMessages(false); // Clear loading state

        // --- Action Cable Subscription ---
        console.log(
          `Attempting to subscribe to ConversationChannel with id: ${convoId}`
        );
        conversationSubscriptionRef.current = consumer.subscriptions.create(
          // Specify the channel name and identifier parameter (conversation_id)
          { channel: "ConversationChannel", conversation_id: convoId },
          {
            // Lifecycle callbacks
            connected() {
              console.log(`Connected to ConversationChannel ${convoId}`);
              // Optional: Send a "user_online" or "viewing_conversation" presence update to the channel
              // this.perform('receive', { action: 'user_presence', status: 'online', conversation_id: convoId });
              // Or a "user_viewing" message
              // this.perform('receive', { action: 'viewing', conversation_id: convoId });
            },
            disconnected(closeEvent) {
              console.log(
                `Disconnected from ConversationChannel ${convoId}.`,
                closeEvent
              );
              // TODO: Handle disconnection (e.g., show a message to the user, attempt to reconnect)
            },
            received(data) {
              console.log("Received data from Action Cable:", data);
              // Process the received real-time data to update the UI
              handleActionCableMessage(data);
            },
            // Methods to send data TO the backend channel (map to backend ConversationChannel#receive method)
            // Example: Method to send typing status
            sendTypingStatus(isTyping) {
              // Use perform or send to send data to the backend channel's 'receive' method
              // The 'receive' method on the backend will then handle this action based on the 'action' key
              this.perform("typing", {
                action: "typing_status",
                is_typing: isTyping,
              });
              // Or simply: this.send({ action: 'typing_status', is_typing: isTyping });
            },
            // Add other methods here for client-to-server real-time actions (e.g., marking message as read)
            markMessageAsRead(messageId) {
              this.perform("mark_as_read", {
                message_id: messageId,
              });
            },
          }
        );
      } catch (error) {
        console.error(
          `Error fetching conversation ${convoId} or subscribing:`,
          error
        );
        setIsLoadingMessages(false); // Clear loading state
        setMessagesLoadError("Failed to load messages."); // Set error state
        // TODO: Implement user-facing error message display for failed message load
        // Optionally, reset activeConversationId or show a specific error screen
      }
    };

    // Before fetching and subscribing to the new conversation, unsubscribe from the previous one
    unsubscribeFromPrevious();

    // Fetch data and subscribe for the new active conversation
    fetchAndSubscribeToConversation(activeConversationId);

    // Cleanup function for the effect: Unsubscribe when component unmounts or activeConversationId changes again
    return () => unsubscribeFromPrevious();
  }, [activeConversationId, isUserReady, isLoadingUsers, messageSearchTerm]);

  useEffect(() => {
    // Function to send the mark as read signal
    const sendMarkAsReadSignal = () => {
      // Get the conversation data from state using activeConversationId
      const conversation = conversations[activeConversationId];

      // Ensure we have a valid active conversation, it's not loading messages, and it has messages
      if (
        activeConversationId &&
        conversation &&
        !isLoadingMessages &&
        conversation.messages &&
        conversation.messages.length > 0
      ) {
        // Find the latest message ID
        const latestMessage = conversation.messages.findLast(
          (msg) => msg.type !== "date"
        );

        if (latestMessage) {
          const latestMessageId = latestMessage.id;

          // Check if the ConversationChannel subscription is active
          const conversationSubscription = conversationSubscriptionRef.current;

          if (
            conversationSubscription &&
            conversationSubscription.perform &&
            conversationSubscription.connected
          ) {
            // Call the markMessageAsRead method defined on the subscription object
            conversationSubscription.markMessageAsRead(latestMessageId);
          } else {
            console.warn(
              `ConversationChannel subscription for ${activeConversationId} not ready to send 'mark_as_read'.`
            );
          }
        }
      }
    };

    const readTimeout = setTimeout(() => {
      sendMarkAsReadSignal();
    }, 200); // Small delay to allow subscription to connect after messages load

    // Cleanup function to clear the timeout if the effect runs again (e.g., active convo changes quickly)
    return () => clearTimeout(readTimeout);
  }, [
    activeConversationId,
    isLoadingMessages,
    conversations,
    conversationSubscriptionRef,
  ]); // Dependencies: Watch activeConvoId, message loading, conversations state, and subscription ref

  // Effect for UserChannel subscription
  useEffect(() => {
    const unsubscribeFromUserChannel = () => {
      if (userSubscriptionRef.current) {
        // console.log(`Unsubscribing from UserChannel ${userSubscriptionRef.current.identifier?.user_id}`);
        userSubscriptionRef.current.unsubscribe();
        userSubscriptionRef.current = null;
      }
    };

    if (!isUserReady || !currentUserId) {
      unsubscribeFromUserChannel();
      return;
    }

    // Create UserChannel subscription
    // console.log(`Subscribing to UserChannel for user ${currentUserId}`);
    userSubscriptionRef.current = consumer.subscriptions.create(
      { channel: "UserChannel", user_id: currentUserId },
      {
        connected() {
          /* console.log(...) */
        },
        disconnected(closeEvent) {
          /* console.log(...) */
        },
        received(data) {
          handleActionCableMessage(data);
        }, // Handle user-specific broadcasts

        // Methods to perform actions TO UserChannel (less common than ConversationChannel)
        // Example: App-wide status update if implemented
        // setAwayStatus() { this.perform('set_status', { status: 'away' }); },
      }
    );

    return () => unsubscribeFromUserChannel(); // Cleanup
  }, [isUserReady, currentUserId, handleActionCableMessage]); // Dependencies

  // Effect for PresenceChannel subscription
  useEffect(() => {
    const unsubscribeFromPresenceChannel = () => {
      if (presenceSubscriptionRef.current) {
        // console.log(`Unsubscribing from PresenceChannel`);
        presenceSubscriptionRef.current.unsubscribe();
        presenceSubscriptionRef.current = null;
      }
    };

    if (!isUserReady) {
      unsubscribeFromPresenceChannel();
      return;
    }

    // Create PresenceChannel subscription
    // console.log(`Subscribing to PresenceChannel`);
    presenceSubscriptionRef.current = consumer.subscriptions.create(
      { channel: "PresenceChannel" }, // No parameters needed as per doc
      {
        connected() {
          /* console.log(...) */
        },
        disconnected(closeEvent) {
          /* console.log(...) */
        },
        received(data) {
          handleActionCableMessage(data);
        }, // Handle app-wide presence broadcasts
      }
    );

    return () => unsubscribeFromPresenceChannel(); // Cleanup
  }, [isUserReady, handleActionCableMessage]); // Dependencies

  // --- Memoized Values (Derived state for rendering efficiency) ---

  // Get the active conversation object from the conversations state
  const activeConversation = conversations[activeConversationId];
  // Ensure messages array is accessible even if empty or undefined initially
  const activeMessages = activeConversation?.messages || [];
  // Get the pinned message ID for the active conversation
  const pinnedMessageIdFromConvo = activeConversation?.pinnedMessageId;

  // Memoized computation for the pinned message object (used by ChatBody)
  const pinnedMessage = useMemo(() => {
    // Wait for pinnedMessageId and the active conversation's messages to be available
    if (
      !pinnedMessageIdFromConvo ||
      !activeConversation?.messages ||
      isLoadingUsers
    )
      return null; // Also wait for users, as processing uses user data

    // Find the pinned message within the active conversation's messages array
    return (
      activeConversation.messages.find(
        (msg) => msg.id === pinnedMessageIdFromConvo
      ) || null
    );
  }, [pinnedMessageIdFromConvo, activeConversation?.messages, isLoadingUsers]);

  // Memoized computation for the message being replied to (used by MessageInput)
  const replyPreviewMessage = useMemo(() => {
    // Wait for replyingToMessage state and the active conversation's messages to be available
    if (!replyingToMessage || !activeConversation?.messages || isLoadingUsers)
      return null; // Also wait for users

    // Find the message being replied to within the active conversation's messages array
    return (
      activeConversation.messages.find(
        (msg) => msg.id === replyingToMessage.id
      ) || null
    );
  }, [replyingToMessage, activeConversation?.messages, isLoadingUsers]);
  // Memoized computation for conversation header details (using active conversation and allUsers)
  const getActiveConversationHeaderDetails = useCallback(() => {
    // You already have this log
    const convo = conversations[activeConversationId];
    // Return empty details if no conversation is active or users are still loading
    if (!convo || isLoadingUsers) {
      return { name: "", avatar: "", status: "" };
    }

    if (convo.type === "group") {
      // Extract participant avatar URLs from the participants array
      // Filter out any null or empty avatar URLs
      const participantAvatars =
        convo.participants
          ?.map((p) => {
            if (!p || !p.id || (!p.avatar && !p.name)) {
              console.warn(
                "Skipping malformed participant object with no ID, avatar, or name:",
                p
              );
              return null; // Skip this participant
            }

            if (p.avatar) {
              // If avatar URL exists, return type 'avatar' with the URL
              return { type: "avatar", data: p.avatar, id: p.id }; // Include ID for potential key in list rendering
            } else {
              // If no avatar, calculate initials using only the 'name' property
              const name = p.name?.trim() || ""; // Use p.name and trim, default to empty string if null/undefined

              let initials = "";
              if (name) {
                const nameParts = name
                  .split(" ")
                  .filter((part) => part.length > 0); // Split by spaces and remove empty parts
                if (nameParts.length > 0) {
                  initials += nameParts[0][0].toUpperCase(); // First letter of the first word
                  // Get first letter of the last word if there is more than one word
                  if (nameParts.length > 1) {
                    initials +=
                      nameParts[nameParts.length - 1][0].toUpperCase();
                  }
                } else if (name.length > 0) {
                  // Handles case where name has content but splitting by space resulted in no parts (e.g., just spaces?)
                  // Or a single character name like "A"
                  initials = name[0].toUpperCase(); // Just use the first character if name has content but no "words"
                }
                // If name is empty or only contains whitespace, initials remains ''
              }

              // Final check: if initials calculation resulted in an empty string
              if (!initials) {
                initials = "?"; // Fallback to '?' if no initials could be generated
                // Log a warning if we expected a name but couldn't get initials
                if (p.name) {
                  console.warn(
                    `Participant with ID ${p.id} has name "${p.name}" but no usable parts for initials, using '?'.`
                  );
                } else {
                  console.warn(
                    `Participant with ID ${p.id} has no name, using '?'.`
                  );
                }
              }

              return { type: "initials", data: initials, id: p.id }; // Return type 'initials' with the calculated text
            }
          })
          .filter(Boolean) || // Filter out any null results from the map (malformed participants)
        []; // Default to empty array if convo.participants is null/undefined

      console.log(
        "Group Participant Avatars/Initials Data:",
        participantAvatars
      );
      console.log(convo.avatar);

      return {
        name: convo.title || "Group Chat", // Use the group's title
        avatar: convo.avatar, // Still set avatarUrl to empty for groups, as we use participantAvatars
        participantAvatars: participantAvatars, // <--- Provide array of avatar/initials objects
        // Add participant count to the status for groups
        status: `${convo.participants?.length || 0} members, ${
          convo.messages?.length || 0
        } messages`,
        conversationType: "group", // <--- Indicate conversation type
      };
    } else {
      // For direct messages, find the *other* participant
      const participant = convo.participants?.find(
        (p) => p.id !== currentUserId
      ); // Use convo object here
      //  const otherParticipantId = otherParticipant?.id; // Get the ID safely

      // Return details using the participant data from allUsers
      return {
        name: participant.name,
        avatar: participant.avatar,
        status: participant.lastSeen || "Offline",
        conversationType: "direct", // <--- Indicate conversation type
        participantAvatars: [],
      };
    }
  }, [
    activeConversationId,
    conversations,
    currentUserId,
    allUsers,
    isLoadingUsers,
  ]); // Dependencies

  const headerDetails = getActiveConversationHeaderDetails(); // Call the memoized getter

  // Memoized computation to process messages for display in ChatBody
  // Maps backend message data to frontend display structure (adds sender details, reply info, processes attachments)
  const processedMessages = useMemo(() => {
    // Wait for the active conversation's messages to be loaded and users to be loaded
    if (
      !activeConversationId ||
      !conversations[activeConversationId]?.messages ||
      isLoadingUsers
    )
      return []; // Wait for messages array and users

    const activeConvo = conversations[activeConversationId];
    const messagesToProcess = activeConvo.messages; // messages array is guaranteed to exist here

    const participants = activeConvo.participants;
    return messagesToProcess.map((msg) => {
      // NOTE: If backend doesn't send 'date' type messages, you'll need to
      // generate date separators in the frontend based on message timestamps before this mapping.
      if (msg.type === "date") {
        return msg; // Keep existing frontend date separator if used
      }

      // Find the sender's user object using the fetched allUsers state
      // const sender = participants.find((p) => p.id === msg.senderId);

      const isOutgoing = msg.isOutgoing || msg.senderId === currentUserId; // Determine if the message is outgoing

      // Process reply information if the message is a reply
      let replyTo = null;
      if (msg.replyToId) {
        // Find the original message being replied to within the current conversation's messages
        const originalMsg = activeConvo.messages?.find(
          (m) => m.id === msg.replyToId
        ); // Use activeConvo directly

        if (originalMsg && originalMsg.type !== "date") {
          // Ensure the original message is not a date separator
          // Find the sender of the original message using allUsers
          const originalSender = allUsers[originalMsg.senderId] || {
            name: "Unknown",
          }; // Use allUsers for lookup
          let originalText = originalMsg.text || ""; // Get original message text

          // Generate a preview string for the original message if it has no text (e.g., media, audio)
          if (!originalText) {
            if (originalMsg.type === "audio") {
              originalText = "Audio Message";
            } else if (
              originalMsg.attachments &&
              originalMsg.attachments.length > 0
            ) {
              const firstAttach = originalMsg.attachments[0];
              // Use backend attachment name if available, fallback to type
              originalText =
                firstAttach.name ||
                `${
                  firstAttach.type.charAt(0).toUpperCase() +
                  firstAttach.type.slice(1)
                } Attachment`;

              if (originalMsg.attachments.length > 1) {
                originalText += ` (+${originalMsg.attachments.length - 1})`; // Indicate multiple attachments
              }
            } else {
              originalText = "Message"; // Generic fallback
            }
          }

          // Structure the replyTo object for the frontend UI
          replyTo = {
            senderName:
              originalSender.id === currentUserId ? "You" : originalSender.name, // Use 'You' for current user's replies
            originalText: originalText, // The preview text of the original message
          };
        }
      }

      const processedAttachments =
        msg.attachments?.map((att) => ({
          ...att,
          src: att.url,
          type: att.type,
          // Map backend 'altText' to frontend 'alt'
          alt: att.altText,
          thumbnailUrl:
            att.thumbnailUrl ||
            (att.type === "video" || att.type === "image"
              ? att.url
              : undefined) ||
            (att.type === "video" ? videoThumb1 : "image_placeholder.jpg"), // Adjust placeholders paths
          isUploading: att.isUploading,
        })) || []; // Ensure attachments is always an array

      const displayTimestamp = msg.timestamp;

      return {
        ...msg,
        isOutgoing,
        replyTo,
        attachments: processedAttachments,
        timestamp: displayTimestamp,

        linkPreview: msg.linkPreview
          ? {
              url: msg.linkPreview.url,
              title: msg.linkPreview.title,
              description: msg.linkPreview.description,
              imageUrl: msg.linkPreview.image_url, // Use backend's image_url field
            }
          : null,
        // Include temporary UI status field if added during optimistic update
        isSending: msg.isSending,
      };
    });
    // Dependencies: Re-compute if messages, user ID, or user loading status changes
  }, [
    activeConversationId,
    conversations,
    currentUserId,
    allUsers,
    isLoadingUsers,
  ]);

  // Memoized computation for the processed pinned message (used by ChatBody)
  const processedPinnedMessage = useMemo(() => {
    // Wait for the pinned message object, active conversation messages, and users to be available
    if (!pinnedMessage || !activeConversation?.messages || isLoadingUsers)
      return null;

    // Find the processed version of the pinned message from the processedMessages array
    // processedMessages memo already includes sender details and attachment processing
    const processedMsg = processedMessages.find(
      (msg) => msg.id === pinnedMessage.id
    );
    if (!processedMsg) return null; // This should ideally not happen if pinnedMessage is found

    // Generate a preview text for the pinned message based on its content type
    let text = processedMsg.text || "";
    if (!text) {
      // Use the same logic as in processedMessages to get a preview string
      if (processedMsg.type === "audio") text = "Audio Message";
      else if (processedMsg.attachments?.length > 0)
        text =
          processedMsg.attachments[0].name ||
          `${processedMsg.attachments[0].type} Attachment`;
      else text = "Message";
    }
    // Structure the pinned message data for the ChatBody component
    return {
      id: processedMsg.id,
      text: text,
    };
  }, [pinnedMessage, processedMessages, isLoadingUsers]);

  // Memoized computation for the processed reply preview message (used by MessageInput)
  const processedReplyPreview = useMemo(() => {
    // Wait for the message being replied to, active conversation messages, and users to be available
    if (!replyPreviewMessage || !activeConversation?.messages || isLoadingUsers)
      return null;

    const processedMsg = processedMessages.find(
      (msg) => msg.id === replyPreviewMessage.id
    );
    if (!processedMsg) return null;

    // Generate a preview text for the reply message based on its content type
    let text = processedMsg.text || "";
    if (!text) {
      // Use the same logic as in processedMessages to get a preview string
      if (processedMsg.type === "audio") text = "Audio Message";
      else if (processedMsg.attachments?.length > 0)
        text =
          processedMsg.attachments[0].name ||
          `${processedMsg.attachments[0].type} Attachment`;
      else text = "Message";
    }
    // Determine the sender's display name for the reply preview ('You' or their name)
    const senderName =
      processedMsg.sender.id === currentUserId
        ? "You"
        : processedMsg.sender.name;
    // Structure the reply preview data for the MessageInput component
    return {
      id: processedMsg.id,
      senderName: senderName,
      text: text,
    };
    // Dependencies: Re-compute if replyPreviewMessage object, processedMessages array, current user ID, or user loading status changes
  }, [replyPreviewMessage, processedMessages, currentUserId, isLoadingUsers]);

  // --- Event Handlers (Triggered by UI or other logic) ---

  // Handler for managing context menu visibility and position
  const handleContextMenuCallback = useCallback((event, messageId) => {
    // If a messageId is provided and an event, show the context menu at the event's coordinates
    if (messageId && event) {
      event.preventDefault(); // Prevent the default browser context menu
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        messageId: messageId, // Store the ID of the message the menu is for
      });
      console.log(
        "Context Menu Triggered for message:",
        messageId,
        "at",
        event.clientX,
        event.clientY
      );
    } else {
      // If no messageId or event is provided, hide the context menu
      setContextMenu({ visible: false, x: 0, y: 0, messageId: null });
    }
  }, []); // No dependencies

  // Handler for selecting a conversation from the sidebar
  const handleSelectConversation = useCallback(
    (convoId) => {
      // Only update if a different conversation is selected
      // console.log('handleSelectConversation in ChatInterface called with ID:', convoId);
      console.log(conversations);

      if (convoId !== activeConversationId) {
        console.log(
          "Changing activeConversationId from",
          activeConversationId,
          "to",
          convoId
        );
        setActiveConversationId(convoId); // Update the active conversation ID state
        setShowSidebarOnMobile(false); // Hide sidebar on mobile when conversation is selected
        // Reset UI state specific to the previous conversation when switching
        setReplyingToMessage(null); // Clear any ongoing reply
        setSelectModeActive(false); // Exit select mode
        setSelectedMessageIds(new Set()); // Clear selected messages
        setContextMenu({ visible: false, x: 0, y: 0, messageId: null }); // Close context menu
        setMessagesLoadError(null); // Clear message errors when changing conversation
      }
    },
    [activeConversationId, conversations]
  ); // Dependency: Only re-create if activeConversationId changes

  // Handler for closing conversation (mobile back button)
  const handleCloseConversation = useCallback(() => {
    setActiveConversationId(null);
    setShowSidebarOnMobile(true); // Show sidebar when conversation is closed
    setReplyingToMessage(null);
    setSelectModeActive(false);
    setSelectedMessageIds(new Set());
    setContextMenu({ visible: false, x: 0, y: 0, messageId: null });
  }, []);

  // Handler for closing sidebar on mobile
  const handleCloseSidebar = useCallback(() => {
    setShowSidebarOnMobile(false);
  }, []);

  // Handler for pinning/unpinning a message
  const handlePinMessage = useCallback(
    async (messageId) => {
      // Ensure user is ready and an active conversation is selected
      if (!isUserReady || !activeConversationId) {
        console.error(
          "Cannot pin message, user not ready or no active conversation."
        );
        handleContextMenuCallback(null, null); // Close context menu
        return;
      }
      const currentConvo = conversations[activeConversationId];
      // Ensure the conversation is loaded in state
      if (!currentConvo) {
        console.error(
          "Cannot pin message, conversation not found in state:",
          activeConversationId
        );
        handleContextMenuCallback(null, null); // Close context menu
        return;
      }

      // Find the message to pin in the conversation's messages
      const messageToPin = currentConvo.messages?.find(
        (msg) => msg.id === messageId
      );
      if (!messageToPin) {
        console.error(
          "Message not found for pinning:",
          messageId,
          "in conversation",
          activeConversationId
        );
        handleContextMenuCallback(null, null); // Close context menu
        return;
      }

      // Determine if the message is currently pinned
      const isCurrentlyPinned = currentConvo.pinnedMessageId === messageId;
      // Determine the new pinned message ID (null to unpin, messageId to pin)
      const newPinnedMessageId = isCurrentlyPinned ? null : messageId;

      setConversations((prev) => {
        const convo = prev[activeConversationId];
        if (!convo) return prev;
        return {
          ...prev,
          [activeConversationId]: {
            ...convo,
            pinnedMessageId: newPinnedMessageId, // Optimistically set the new pinned ID
          },
        };
      });

      // --- Call Backend API to Update Pinned Message ---
      try {
        console.log(
          `Attempting to ${
            newPinnedMessageId ? "pin" : "unpin"
          } message ${messageId} in conversation ${activeConversationId} on backend.`
        );
        const response = await fetchApi(
          `/conversations/${activeConversationId}/pin_message`, // Backend endpoint for pinning
          "POST", // Backend uses POST for this action
          { message_id: newPinnedMessageId } // Request body with the new pinned message ID (or null)
        );

        if (!response.ok) {
          // If the backend API call fails, revert the optimistic UI update
          console.error(
            `Failed to ${
              newPinnedMessageId ? "pin" : "unpin"
            } message ${messageId} on backend. Reverting UI. Status: ${
              response.status
            }`,
            response
          );
          setConversations((prev) => {
            const convo = prev[activeConversationId];
            if (!convo) return prev;
            return {
              ...prev,
              [activeConversationId]: {
                ...convo,
                pinnedMessageId: currentConvo.pinnedMessageId, // Revert to the original pinned ID
              },
            };
          });
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        console.log(
          `Pinned message update request sent to backend. Waiting for Action Cable confirmation.`
        );
      } catch (error) {
        console.error("Error pinning/unpinning message:", error);
        // TODO: Show user-facing error message (e.g., a toast notification)
      } finally {
        handleContextMenuCallback(null, null); // Always close the context menu after action
      }
    },
    [
      activeConversationId,
      conversations,
      handleContextMenuCallback,
      isUserReady,
    ]
  ); // Dependencies: Include relevant states and callbacks

  // Handler for initiating a reply to a specific message
  const handleReplyToMessage = useCallback(
    (messageId) => {
      // Find the message object being replied to within the active conversation's messages
      const messageToReply = conversations[
        activeConversationId
      ]?.messages?.find((msg) => msg.id === messageId);
      // If the message is found, set the replyingToMessage state
      if (messageToReply) {
        setReplyingToMessage(messageToReply); // Store the message object being replied to
        console.log("Replying to message:", messageId);
      } else {
        console.warn(
          "Attempted to reply to message not found in active conversation:",
          messageId
        );
      }
      handleContextMenuCallback(null, null); // Close the context menu
    },
    [activeConversationId, conversations, handleContextMenuCallback]
  ); // Dependencies: Include relevant states and callbacks

  // Handler for cancelling the reply state
  const handleCancelReply = useCallback(() => {
    setReplyingToMessage(null); // Clear the replyingToMessage state
    console.log("Reply Cancelled");
  }, []); // No dependencies

  // Handler for copying a message's text to the clipboard
  const handleCopyMessage = useCallback(
    async (messageId) => {
      // Find the message object to copy from the active conversation's messages
      const messageToCopy = activeConversation?.messages?.find(
        (msg) => msg.id === messageId
      );
      // Ensure the message exists and has text content
      if (messageToCopy?.text) {
        try {
          // Use the Clipboard API to write the text
          await navigator.clipboard.writeText(messageToCopy.text);
          console.log("Message copied to clipboard:", messageToCopy.text);
          // TODO: Optionally, show a success notification (e.g., a toast) to the user
        } catch (err) {
          console.error("Failed to copy message:", err);
          // TODO: Optionally, show an error notification to the user
        }
      } else if (messageToCopy) {
        // Handle cases where the message exists but is not a text message
        console.log(
          "Cannot copy non-text message content directly with this handler. Message type:",
          messageToCopy.type
        );
        // TODO: Implement handling for copying other content types if necessary (e.g., attachment URLs, link preview data)
      } else {
        console.warn("Attempted to copy message not found:", messageId);
      }
      handleContextMenuCallback(null, null); // Close the context menu
    },
    [activeConversation?.messages, handleContextMenuCallback]
  ); // Dependencies: Include active conversation messages and callback

  // Handler for starting message selection mode from the context menu
  const handleStartSelectFromMenu = useCallback(
    (messageId) => {
      // Ensure a message ID is provided
      if (!messageId) return;
      setSelectModeActive(true); // Activate selection mode
      setSelectedMessageIds(new Set([messageId])); // Start selection with the clicked message ID
      handleContextMenuCallback(null, null); // Close the context menu
      console.log("Select Mode Initiated for message:", messageId);
    },
    [handleContextMenuCallback]
  ); // Dependency: Include the context menu callback

  // Handler for toggling the selection status of a message in selection mode
  const handleToggleMessageSelection = useCallback(
    (messageId) => {
      // Only allow toggling if selection mode is active
      if (!selectModeActive) return;

      // Use functional update for selectedMessageIds state
      setSelectedMessageIds((prevSelected) => {
        const newSelected = new Set(prevSelected); // Create a new Set based on the previous state
        if (newSelected.has(messageId)) {
          newSelected.delete(messageId); // Deselect if already selected
          if (newSelected.size === 0) {
            setSelectModeActive(false); // Direct state update
          }
        } else {
          newSelected.add(messageId); // Select if not already selected
        }
        return newSelected; // Return the new Set state
      });
      console.log("Toggled selection for message:", messageId);
    },
    [selectModeActive]
  ); // Dependency: Re-create if selectModeActive changes

  // Handler for cancelling selection mode
  const handleCancelSelectMode = useCallback(() => {
    setSelectModeActive(false); // Deactivate selection mode
    setSelectedMessageIds(new Set()); // Clear all selected messages
    console.log("Select Mode Cancelled");
  }, []); // No dependencies

  // Handler for selecting/deselecting all messages in selection mode
  const handleSelectAllMessages = useCallback(
    (isChecked) => {
      // Only proceed if selection mode is active
      if (!selectModeActive) return;

      if (isChecked) {
        // If checking "Select All", get all message IDs from the active conversation
        const currentMessages =
          conversations[activeConversationId]?.messages || [];
        // Filter out frontend-only date messages if necessary before selecting all
        const allMessageIds = currentMessages
          .filter((msg) => msg.type !== "date")
          .map((msg) => msg.id);
        setSelectedMessageIds(new Set(allMessageIds)); // Set state to include all IDs
        console.log("Selected all messages.");
      } else {
        // If unchecking "Select All", clear the selected message IDs
        setSelectedMessageIds(new Set()); // Clear the Set
        console.log("Deselected all messages.");
      }
      // NOTE: selectModeActive state is managed by handleToggleMessageSelection based on size.
    },
    [selectModeActive, activeConversationId, conversations]
  ); // Dependencies: Include relevant states

  // Handler for deleting selected messages
  // Calls the backend API for each selected message.
  const handleDeleteMessages = useCallback(
    async (messageIdsToDelete) => {
      // Ensure user is ready and an active conversation is selected
      if (!isUserReady || !activeConversationId) {
        console.error(
          "Cannot delete messages, user not ready or no active conversation."
        );
        handleContextMenuCallback(null, null); // Close context menu
        return;
      }

      // Convert the input (Set or array) to an array of IDs
      const idsArray = Array.from(
        messageIdsToDelete instanceof Set
          ? messageIdsToDelete // If input is a Set, convert it
          : new Set(messageIdsToDelete)
      ); // If input is an array/single ID, convert to Set then array

      // If no messages are selected, do nothing and close context menu
      if (idsArray.length === 0) {
        handleContextMenuCallback(null, null);
        return;
      }

      // Confirm deletion with the user
      if (
        !window.confirm(
          `Are you sure you want to delete ${idsArray.length} message(s)?`
        )
      ) {
        handleContextMenuCallback(null, null); // Close context menu if user cancels
        return;
      }

      setConversations((prev) => {
        const convo = prev[activeConversationId];
        // If conversation is not found in state, return previous state
        if (!convo) return prev;

        // Filter the messages array to exclude the IDs to be deleted
        const updatedMessages =
          convo.messages?.filter((msg) => !idsArray.includes(msg.id)) || [];
        // If the pinned message is among those deleted, unpin it optimistically
        let newPinnedId = convo.pinnedMessageId;
        if (newPinnedId && idsArray.includes(newPinnedId)) {
          newPinnedId = null;
        }

        return {
          ...prev,
          [activeConversationId]: {
            ...convo,
            messages: updatedMessages, // Use the filtered messages array
            pinnedMessageId: newPinnedId, // Use the potentially updated pinned ID
            // lastPreview and lastTime will be updated by Action Cable
          },
        };
      });

      // --- Call Backend API for Each Message Deletion ---
      // Iterate through the array of message IDs to delete and make a DELETE request for each.
      const deletionPromises = idsArray.map(async (messageId) => {
        console.log(
          `Attempting to delete message ${messageId} in conversation ${activeConversationId} on backend.`
        );
        try {
          const response = await fetchApi(
            `/conversations/${activeConversationId}/messages/${messageId}`, // Backend endpoint for deleting a message
            "DELETE" // Use the DELETE HTTP method
          );

          if (!response.ok) {
            console.error(
              `Failed to delete message ${messageId} on backend. Status: ${response.status}`,
              response
            );
          } else {
            console.log(`Message ${messageId} delete request sent to backend.`);
          }
        } catch (error) {
          console.error(
            `Error sending delete request for message ${messageId}:`,
            error
          );
          // TODO: Show user-facing error message for this specific message deletion failure.
          // Same handling as above - mark as failed delete attempt in UI.
        }
      });
      console.log("All delete message requests initiated.");

      if (replyingToMessage && idsArray.includes(replyingToMessage.id)) {
        setReplyingToMessage(null);
      }
      // Exit selection mode and clear selected messages
      setSelectModeActive(false);
      setSelectedMessageIds(new Set());
      handleContextMenuCallback(null, null); // Close the context menu
      // Dependencies: Include relevant states and callbacks
    },
    [
      activeConversationId,
      replyingToMessage,
      handleContextMenuCallback,
      isUserReady,
      selectedMessageIds,
    ]
  );

  const handleDeleteConversationRequest = useCallback(
    async (convoIdToDelete) => {
      if (!currentUserId || !conversations[convoIdToDelete]) {
        console.error(
          "Cannot delete conversation, user not ready or conversation not found:",
          convoIdToDelete
        );
        return;
      }

      const conversationToDelete = conversations[convoIdToDelete];
      if (!conversationToDelete) {
        console.error(
          "Conversation details not found for optimistic delete:",
          convoIdToDelete
        );
        return;
      }
      console.log(
        "Attempting to delete conversation (optimistic frontend + backend API):",
        convoIdToDelete
      );

      setConversations((prev) => {
        const { [convoIdToDelete]: _, ...remainingConvos } = prev;

        if (String(activeConversationId) === String(convoIdToDelete)) {
          const firstRemainingId =
            Object.entries(remainingConvos).find(
              ([id, convo]) => !convo.isArchived
            )?.[0] || null;

          setTimeout(() => {
            setActiveConversationId(firstRemainingId);
            setReplyingToMessage(null);
            setSelectModeActive(false);
            setSelectedMessageIds(new Set());
          }, 0);
        } else {
          if (
            replyingToMessage &&
            String(replyingToMessage.conversationId) === String(convoIdToDelete)
          ) {
            setReplyingToMessage(null);
          }
        }

        return remainingConvos;
      });

      try {
        console.log(
          `Sending DELETE request to /conversations/${convoIdToDelete}`
        );
        const response = await fetchApi(
          `/conversations/${convoIdToDelete}`,
          "DELETE"
        );

        if (!response.ok) {
          console.error(
            `Backend deletion failed for ${convoIdToDelete}. Status: ${response.status}. Reverting frontend state.`,
            response
          );

          setConversations((prev) => ({
            ...prev,
            [convoIdToDelete]: conversationToDelete,
          }));

          if (String(activeConversationId) === String(convoIdToDelete)) {
            console.warn(
              "Attempted to revert active conversation state, manual handling may be needed."
            );
          }
        } else {
          console.log(`Backend deletion confirmed for ${convoIdToDelete}.`);
        }
      } catch (error) {
        console.error(
          `Network error during deletion for ${convoIdToDelete}. Reverting frontend state.`,
          error
        );

        setConversations((prev) => ({
          ...prev,
          [convoIdToDelete]: conversationToDelete,
        }));

        if (String(activeConversationId) === String(convoIdToDelete)) {
          console.warn(
            "Attempted to revert active conversation state due to network error, manual handling may be needed."
          );
        }
      }
    },
    [
      activeConversationId,
      conversations,
      replyingToMessage,
      currentUserId,
      setConversations,
      setActiveConversationId,
      setReplyingToMessage,
      setSelectModeActive,
      setSelectedMessageIds,
      fetchApi,
    ]
  );

  // Handler for archiving or unarchiving a conversation
  // Calls the backend API to update the conversation's archived status.
  const handleArchiveConversation = useCallback(
    async (convoIdToArchive) => {
      // Ensure user is ready and the conversation exists in state
      if (!isUserReady || !conversations[convoIdToArchive]) {
        console.error(
          "Cannot archive/unarchive conversation, user not ready or conversation not found:",
          convoIdToArchive
        );
        return;
      }

      const currentConvo = conversations[convoIdToArchive];
      // Determine the target archived status (will be archived if currently not, and vice versa)
      const willBeArchived = !currentConvo.isArchived;
      console.log(
        `${willBeArchived ? "Archiving" : "Unarchiving"} conversation:`,
        convoIdToArchive
      );

      // --- Optimistic UI Update ---
      // Update the isArchived status locally before the API call.
      // Action Cable broadcast from the backend will eventually confirm/correct this state.
      setConversations((prev) => {
        const convo = prev[convoIdToArchive];
        // If conversation is not found in state, return previous state
        if (!convo) return prev;

        const updated = {
          ...prev,
          [convoIdToArchive]: {
            ...convo,
            isArchived: !convo.isArchived, // Optimistically toggle the archived status
          },
        };
        // If we are optimistically archiving the *active* conversation, find the next available
        // non-archived conversation in the *optimistic* state and set it as the active one.
        if (activeConversationId === convoIdToArchive && willBeArchived) {
          // Find the ID of the first non-archived conversation in the updated state (excluding the one being archived)
          const firstNonArchivedId =
            Object.entries(updated)
              .filter(([id, c]) => id !== convoIdToArchive && !c.isArchived) // Use 'c' to avoid shadowing 'convo' from outer scope
              .map(([id]) => id)[0] || null; // Get the first ID or null

          // Update the active conversation ID state after the setConversations update.
          // Use setTimeout to defer the state update for robustness as mentioned above.
          setTimeout(() => {
            setActiveConversationId(firstNonArchivedId); // Set the new active conversation ID
            // Reset UI state for the old conversation (which is now archived)
            setReplyingToMessage(null);
            setSelectModeActive(false);
            setSelectedMessageIds(new Set());
            setContextMenu({ visible: false, x: 0, y: 0, messageId: null });
          }, 0); // Using 0 timeout defers the state update
        }

        return updated; // Return the updated conversations state
      });

      // --- Call Backend API to Archive/Unarchive ---
      try {
        // Determine the correct backend endpoint based on whether archiving or unarchiving
        const endpoint = willBeArchived ? "archive" : "unarchive";
        console.log(
          `Attempting to call backend endpoint: /conversations/${convoIdToArchive}/${endpoint}`
        );
        const response = await fetchApi(
          `/conversations/${convoIdToArchive}/${endpoint}`, // Backend endpoint (e.g., /conversations/1/archive)
          "POST" // Backend uses POST for archive/unarchive actions
        );

        if (!response.ok) {
          // If the backend API call fails, revert the optimistic UI update
          console.error(
            `Failed to ${endpoint} conversation ${convoIdToArchive} on backend. Reverting UI. Status: ${response.status}`,
            response
          );
          setConversations((prev) => {
            const convo = prev[convoIdToArchive];
            if (!convo) return prev;
            // Revert the isArchived status to its state before the toggle
            return {
              ...prev,
              [convoIdToArchive]: {
                ...convo,
                isArchived: !willBeArchived, // Revert to the original status
              },
            };
          });
          // If the failed operation was archiving the active convo and we switched, switch back or handle error
          // This requires more complex state management or re-fetching. For now, rely on error message.
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        console.log(
          `Conversation ${convoIdToArchive} ${endpoint} request sent to backend.`
        );
        // If the API call is successful, the backend is expected to broadcast a 'conversation_updated'
        // Action Cable message with the new 'isArchived' status.
        // The handleActionCableMessage function will process this broadcast, confirming/correcting the UI state.
      } catch (error) {
        console.error(
          `Error ${willBeArchived ? "archiving" : "unarchiving"} conversation:`,
          error
        );
        // TODO: Show user-facing error message (e.g., a toast notification)
      }
      // Dependencies: Include relevant states
    },
    [activeConversationId, conversations, isUserReady]
  );

  // Handler for starting a new conversation (specifically direct messages for now)
  // Calls the backend API to create a new conversation.
  const handleStartNewConversation = useCallback(
    async (contactUserId) => {
      console.log(
        "Attempting to start new conversation with contact:",
        contactUserId
      );
      // Ensure user is ready before attempting to create a conversation
      if (!isUserReady) {
        console.error("User not ready, cannot start conversation.");
        // TODO: Inform user or handle authentication flow
        return;
      }

      // --- Frontend Check for Existing Direct Conversation ---
      // Before calling the backend, check if a direct conversation already exists with this user in the current state.
      // This avoids unnecessary backend calls if the chat already exists.
      const existingConvo = Object.values(conversations).find(
        (convo) =>
          convo.type === "direct" && // Ensure it's a direct message
          convo.participants?.some((p) => p.id === contactUserId) && // Check if the contact is a participant
          convo.participants?.some((p) => p.id === currentUserId) // Crucially, also check if the current user is a participant
      );

      if (existingConvo) {
        console.log("Found existing direct conversation:", existingConvo.id);
        handleSelectConversation(existingConvo.id); // Select the existing conversation
        return; // Stop the process
      }

      // --- Call Backend API to Create New Conversation ---
      // If no existing direct conversation found, call the backend to create a new one.
      // Backend expects participant_ids array *excluding* the current user (as current_user is added automatically).
      const participantIds = [contactUserId]; // Array containing only the ID of the other participant

      try {
        console.log(
          "Creating new direct conversation with user IDs:",
          participantIds
        );
        const response = await fetchApi("/conversations", "POST", {
          // Backend endpoint for creating conversations
          // Request body structure matching backend documentation
          conversation: {
            conversation_type: "direct", // Hardcoded for direct for now
            participant_ids: participantIds, // Pass the IDs of other participants
            // 'title' is not required for direct chats as per backend docs
          },
        });

        if (!response.ok) {
          // Handle backend validation errors (422) or other errors
          const errorData = await response.json().catch(() => ({})); // Attempt to parse error body for more details
          console.error(
            "Failed to create new conversation on backend:",
            response.status,
            errorData
          );
          // TODO: Show user-facing error message, potentially using errorData.errors
          alert(
            `Failed to create conversation: ${
              errorData.errors
                ? JSON.stringify(errorData.errors)
                : response.statusText
            }`
          );
          return; // Stop if creation failed
        }

        const newConversation = await response.json(); // Backend returns the created Conversation Detail object

        // --- Update Frontend State with New Conversation ---
        // Add the newly created conversation (including its ID from the backend) to the state
        setConversations((prev) => ({
          ...prev,
          [newConversation.id]: newConversation, // Add the full detail object keyed by its new ID
        }));

        console.log(
          "Created and added new conversation to state:",
          newConversation.id
        );
        // Select the newly created conversation to open it in the chat interface
        handleSelectConversation(newConversation.id);
      } catch (error) {
        console.error("Error creating new conversation:", error);
        // TODO: Show user-facing error message (e.g., toast)
        alert(`Error creating conversation: ${error.message}`);
      }
      // Dependencies: Include relevant states and user ID
    },
    [conversations, handleSelectConversation, currentUserId, isUserReady]
  );

  const handleCreateGroupConversation = useCallback(
    async (groupCreationData) => {
      console.log(
        "Attempting to create new group conversation (using FormData):",
        groupCreationData
      );
      if (!isUserReady) {
        console.error("User not ready, cannot create group conversation.");
        return;
      }

      const { name, avatarFile, memberIds } = groupCreationData;

      //   if (!name || name.trim() === "") {
      //     console.error("Group name is required.");
      //     alert("Group name is required."); // User feedback
      //     return;
      //   }

      if (!memberIds || memberIds.length === 0) {
        console.error("At least one member is required for group creation.");
        alert("Please select at least one member."); // User feedback
        return;
      }

      // --- 1. Prepare FormData for the Initial Request ---
      // We will send all data, including the avatar file, in one FormData request.
      const formData = new FormData();

      // Append conversation attributes, likely nested under 'conversation' key as backend expects
      // Backend: Your Rails controller will receive params like { conversation: { title: '...', conversation_type: '...', participant_ids: [...] } }
      formData.append("conversation[conversation_type]", "group");
      formData.append("conversation[title]", name.trim());

      // Append participant IDs. For arrays in FormData, you typically append each item with '[]' suffix.
      // Backend: participant_ids will be an array like ['1', '5', ...]
      memberIds.forEach((memberId) => {
        formData.append("conversation[participant_ids][]", memberId);
      });

      // Append the avatar file if it exists
      // Backend: Your controller will receive the file via params[:conversation][:avatar]
      if (avatarFile) {
        formData.append("conversation[avatar]", avatarFile);
        console.log("Appending avatar file to FormData:", avatarFile);
      } else {
        console.log("No avatar file to append to FormData.");
      }

      // --- 2. Send the POST /conversations Request with FormData ---
      try {
        console.log("Sending POST /conversations request with FormData.");
        // Use fetchApi to send the POST request
        // Pass the FormData object and true as the fourth argument to indicate FormData
        const response = await fetchApi(
          "/conversations",
          "POST",
          formData,
          true
        );

        // Check if the request was successful (backend usually returns 201 Created)
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(
            "Failed to create group conversation on backend:",
            response.status,
            errorData
          );
          alert(
            `Failed to create group conversation: ${
              errorData.errors
                ? JSON.stringify(errorData.errors)
                : response.statusText
            }`
          );
          return; // Stop if creation failed
        }

        // Parse the response. Backend should return the created Conversation Detail object.
        const newGroupConversation = await response.json();

        console.log(
          "Group conversation created on backend with ID:",
          newGroupConversation.id
        );

        // --- 3. Update Frontend State and Select the New Conversation ---
        // Add the newly created conversation to the state
        // The response object should be the full Conversation Detail structure, now including avatar URL if upload succeeded
        setConversations((prev) => ({
          ...prev,
          [newGroupConversation.id]: newGroupConversation, // Add the new convo object keyed by its ID
        }));

        console.log(
          "Added new group conversation to state:",
          newGroupConversation.id
        );
        // Select the newly created conversation
        setActiveConversationId(newGroupConversation.id);
      } catch (error) {
        console.error("Error creating group conversation:", error);
        alert(`Error creating group conversation: ${error.message}`);
      }
      // Note: The separate avatar upload block is removed now.
    },
    [isUserReady, setConversations, setActiveConversationId]
  ); // Dependencies

  const handleSendMessage = useCallback(
    async (messageData) => {
      // messageData now comes from MessageInput *after* it has completed uploads.
      // It includes text/caption and an array of CONFIRMED backend attachment data.

      if (!isUserReady || !activeConversationId) {
        console.error(
          "Cannot send message, user not ready or no active conversation."
        );
        return;
      }

      // --- 1. Optimistic UI Update (Add temporary message to UI immediately) ---
      // Use data received from MessageInput, which includes confirmed backend attachment info
      const tempMessageId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      console.log(messageData);

      let temporaryMessage = {
        id: tempMessageId, // Temporary client-side ID
        type: messageData.type, // Use type from MessageInput (text, media, audio)
        senderId: currentUserId, // Set sender to the current user
        text: messageData.content.text || null, // Use text/caption from MessageInput
        timestamp: new Date().toISOString(), // Temporary timestamp
        readReceipt: "sending", // Custom status for UI feedback
        replyToId: replyingToMessage ? replyingToMessage.id : null, // Include reply ID
        // Use confirmed attachment data from MessageInput's payload
        attachments: messageData.content.attachments
          ? messageData.content.attachments.map((att) => ({
              // Map confirmed backend data to temporary message structure
              id: att.url[1].id, // This is already the backend Attachment ID
              type: att.url[1].attachment_type,
              name: att.url[1].name,
              size: att.url[1].size_bytes,
              url: att.url[0], // This is the final URL from backend confirmation
              // Add any other backend confirmed properties like thumbnailUrl, displaySize etc.
              // status and progress are not needed here as uploads are done
              status: "uploaded", // Already uploaded
              progress: 100,
              error: null,
            }))
          : [],
        // Use confirmed audio URL from MessageInput's payload if structured this way
        audioUrl:
          messageData.type === "audio" && messageData.content?.url
            ? messageData.content.url
            : undefined,
        isSending: true, // Overall message sending status
      };

      // Basic validation: Ensure there's text OR attachments OR audio
      const hasText =
        temporaryMessage.text && temporaryMessage.text.trim() !== "";
      const hasAttachments =
        temporaryMessage.attachments && temporaryMessage.attachments.length > 0;
      const hasAudio = temporaryMessage.audioUrl;

      const isEmptyMessage = !hasText && !hasAttachments && !hasAudio;

      if (isEmptyMessage) {
        console.warn(
          "Attempted to send empty message after processing. Aborting."
        );
        setReplyingToMessage(null);
        // TODO: Show a notification
        return;
      }

      setReplyingToMessage(null);

      // Scroll to bottom
      if (chatBodyRef.current) {
        requestAnimationFrame(() => {
          if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
          }
        });
      }

      // --- 2. Call Backend API to Create Message Record (Simplified) ---
      // Payload includes text and IDs of already confirmed attachments.
      const messagePayload = {
        message: {
          message_type: temporaryMessage.type,
          text: temporaryMessage.text,
          reply_to_id: temporaryMessage.replyToId,
          // Include the backend Attachment IDs received from MessageInput
          attachment_ids: temporaryMessage.attachments.map((att) => att.id),
          audio_url: temporaryMessage.audioUrl, // Include confirmed audio URL if applicable
        },
      };

      console.log("Sending message payload to backend:", messagePayload);

      try {
        // This endpoint needs to accept the attachment_ids and link the EXISTING Attachment records.
        const messageResponse = await fetchApi(
          `/conversations/${activeConversationId}/messages`, // Backend endpoint
          "POST",
          messagePayload,
          false
        );

        if (!messageResponse.ok) {
          console.error(
            "Failed to create message record on backend. Removing temporary message.",
            messageResponse
          );
          // Handle message creation failure: remove or mark temporary message as failed
          setConversations((prev) => {
            /* ... update message status to failed ... */
          });
          throw new Error(
            `Failed to create message record: HTTP status ${messageResponse.status}`
          );
        }

        // Parse the response to get the created message object from the backend.
        // This response should trigger Action Cable update which replaces the temporary message.
        const createdMessage = await messageResponse.json();
        console.log(
          "Message record created on backend with ID:",
          createdMessage.id
        );

        // Action Cable update handles the final UI display.
        // No further frontend upload orchestration needed here.
      } catch (error) {
        console.error("Overall error sending message:", error);
        // Handle overall message creation failure
        setConversations((prev) => {
          /* ... update overall message status to failed ... */
        });
      } finally {
      }
    },
    [
      activeConversationId,
      currentUserId,
      replyingToMessage,
      isUserReady,
      setConversations,
      fetchApi,
    ]
  ); // Dependencies

  // --- UI State Management ---

  // Effect to close the context menu when clicking outside of it
  useEffect(() => {
    const closeMenu = (event) => {
      // If the context menu is visible
      if (contextMenu.visible) {
        // Get the DOM element of the context menu (ensure you add id="message-context-menu" in your JSX)
        const contextMenuElement = document.getElementById(
          "message-context-menu"
        );
        // If the context menu element exists and the click happened *inside* it, do not close the menu
        if (contextMenuElement && contextMenuElement.contains(event.target)) {
          return;
        }
        // If the click happened outside the menu, close it
        handleContextMenuCallback(null, null);
      }
    };

    // Add event listeners for both left clicks and right clicks to close the menu
    window.addEventListener("click", closeMenu);
    window.addEventListener("contextmenu", closeMenu); // Also close on right-click elsewhere

    // Cleanup function: Remove the event listeners when the component unmounts or the effect re-runs
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("contextmenu", closeMenu);
    };
  }, [contextMenu.visible, handleContextMenuCallback]); 

  const handleAddParticipant = useCallback(async (conversationId, userIdToAdd) => {
    try {
        const response = await fetchApi(
            `/conversations/${conversationId}/add_participant`,
            'POST',
            { user_id: userIdToAdd }
        );

        if (response.ok) {
            console.log('Participant added successfully!');
            // Action Cable will broadcast 'participant_added' to update UI
            return true; // Indicate success
        } else {
            const errorData = await response.json();
            console.error('Failed to add participant:', errorData.error);
            // TODO: Display user-facing error notification
            return false; // Indicate failure
        }
    } catch (error) {
        console.error('Network error during participant addition:', error);
        // TODO: Display user-facing network error notification
        return false; // Indicate failure
    }
  }, [fetchApi]);

  const handleRemoveParticipant = useCallback(async (conversationId, userIdToRemove) => {
    try {
      const response = await fetchApi(
        `/conversations/${conversationId}/remove_participant`,
        'DELETE',
        { user_id: userIdToRemove }
      );

      if (response.ok) {
        console.log('Participant removal successful!');
        // Action Cable broadcasts will update the UI
        return true; // Indicate success
      } else {
        const errorData = await response.json();
        console.error('Failed to remove participant:', response.status, errorData);
        // TODO: Display user-facing error message notification
        return false; // Indicate failure
      }
    } catch (error) {
      console.error('Network or unexpected error during participant removal:', error);
      // TODO: Display user-facing network error message notification
      return false; // Indicate failure
    }
  }, [fetchApi]);

  const updateGroupDetails = useCallback(async (conversationId, {name, avatarFile}) => {
    const formData = new FormData();
  
    if (name !== undefined) {
      formData.append('conversation[title]', name);
    }
  
    if (avatarFile!== undefined) {
      if (avatarFile instanceof File) {
        formData.append('conversation[avatar]', avatarFile);
      } else if (avatarFile === null) {
        formData.append('conversation[avatar]', '');
      }
    }
  
    try {
      const response = await fetchApi(`/conversations/${conversationId}`, "PATCH", formData, true);
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to update group details:', response.status, errorData);
        return false;
      }
  
      const updatedConversationData = await response.json();
      console.log('Group details updated successfully:', updatedConversationData);
      return true;
    } catch (error) {
      console.error('Network or unexpected error during group details update:', error);
      return false;
    }
  }, [fetchApi]);

  const isCurrentUserAdminOrCreator = useMemo(() => {
    if (!activeConversation || activeConversation.type !== 'group' || !currentUserId) {
        return false;
    }

    // Assuming 'creator_id' is a property on your group conversation object
    // Adjust this logic if your group objects have a different way to identify admins/creators
    return activeConversation.is_admin
}, [activeConversation, currentUserId]);



// --- Handlers for Group Management Modal ---
const handleOpenGroupManagementModal = useCallback(() => {
  setShowGroupManagementModal(true);
}, []);

const handleCloseGroupManagementModal = useCallback(() => {
  setShowGroupManagementModal(false);
}, []);

  // --- Loading and Empty States Rendering ---

  // Show loading state if current user, all users, or conversations are still loading
  if (!isUserReady || (isLoadingConversations && !conversations.length) ) {
    return (
      <LoadingState
        message={
          !isUserReady
            ? "Loading user data..."
            : isLoadingUsers
            ? "Loading users..."
            : "Loading conversations..."
        }
      />
    );
  }

  // Show error state if fetching users failed
  if (usersLoadError) {
    return (
      <div className="flex h-[85vh] w-full items-center justify-center text-red-600 font-bold">
        {usersLoadError}
      </div>
    );
  }
  // Show error state if fetching conversations failed
  if (conversationsLoadError) {
    return (
      <div className="flex h-[85vh] w-full items-center justify-center text-red-600 font-bold">
        {conversationsLoadError}
      </div>
    );
  }

  // Determine if there are any non-archived conversations to display in the sidebar
  const hasConversations = Object.values(conversations).some(
    (convo) => !convo.isArchived
  );

  // Prepare the list of available contacts for starting new chats (fetched users excluding current user and groups)
  // Ensure allUsers is an object before attempting to filter its values
  const availableContacts = Object.values(allUsers || {}).filter(
    (user) => user.id !== currentUserId && user.type !== "group"
  ); // Filter out current user and users explicitly marked as 'group'

  // Show the empty state component if no conversations exist or all are archived
  if (!hasConversations) {
    return (
      <EmptyConversationState
        onStartNewConversation={handleStartNewConversation} // Pass the handler for starting a new chat
        onCreateGroupConversation={handleCreateGroupConversation}
        showContacts={showContacts} // Indicate that contacts should be shown to start a chat
        setShowContacts={setShowContacts}
        contacts={availableContacts}
        filter={filter}
        setFilter={setFilter} // Pass the list of available contacts
        // Add missing props for search functionality
        composeSearchTerm={composeSearchTerm}
        onComposeSearchChange={handleComposeSearchChange}
        isLoadingModalSearch={isLoadingModalSearch}
        modalSearchError={modalSearchError}
        typingUsers={typingUsers}
      />
    );
  }

  // Show message load error specifically for the active conversation if it occurred
  if (activeConversationId && messagesLoadError) {
    return (
      <div className="flex-grow flex flex-col items-center justify-center text-red-600 font-bold">
        <p>{messagesLoadError}</p>
        {/* Optional: Add a retry button */}
        <button
          onClick={() =>
            activeConversationId &&
            handleSelectConversation(activeConversationId)
          }
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry Loading Messages
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[85vh] w-full overflow-hidden font-sans antialiased text-gray-900 bg-gray-100">
      {/* Sidebar - Hidden on mobile when conversation is active or sidebar is closed, always visible on desktop */}
      <div className={`w-full max-w-xs flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 ${
        activeConversationId ? 'hidden md:block' : (showSidebarOnMobile ? 'block' : 'hidden md:block')
      }`}>
        <ChatSidebar
          conversations={Object.values(conversations || {}).sort((a, b) => {
            const timeA = new Date(a.lastTime).getTime() || 0;
            const timeB = new Date(b.lastTime).getTime() || 0;
            return timeB - timeA;
          })}
          activeConversationId={activeConversationId}
          onSelectConversation={handleSelectConversation}
          onArchiveConversation={handleArchiveConversation}
          onDeleteConversationRequest={handleDeleteConversationRequest}
          onStartNewConversation={handleStartNewConversation}
          onCreateGroupConversation={handleCreateGroupConversation}
          contacts={availableContacts}
          composeSearchTerm={composeSearchTerm} // <--- Pass the search term state
          onComposeSearchChange={handleComposeSearchChange} // <--- Pass the handler to update search term
          isLoadingModalSearch={isLoadingModalSearch} // <--- Pass the loading state
          modalSearchError={modalSearchError} // <--- Pass the error state
          typingUsers={typingUsers}
          setFilter={setFilter}
          filter={filter}
          onClose={!activeConversationId ? handleCloseSidebar : undefined} // Only show close button when no conversation is active
        />
      </div>

      {/* Chat Area - Hidden on mobile when no conversation and sidebar is shown, always visible on desktop */}
      <div className={`flex-grow flex flex-col overflow-hidden bg-white ${
        activeConversationId ? 'block' : (showSidebarOnMobile ? 'hidden md:flex' : 'block')
      }`}>
        {!activeConversationId && (
          <>
            {/* Desktop empty state */}
            <div className="hidden md:flex flex-grow items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-lg mb-2">No conversation selected</p>
                <p className="text-sm">Select a conversation from the sidebar to start chatting</p>
              </div>
            </div>
            {/* Mobile welcome state when sidebar is closed */}
            <div className="md:hidden flex flex-grow items-center justify-center text-gray-500 px-4">
              <div className="text-center">
                <p className="text-xl font-semibold mb-2 text-gray-700">Welcome to Chat</p>
                <p className="text-sm mb-6 text-gray-500">Start a conversation to begin messaging</p>
                <button
                  onClick={() => setShowSidebarOnMobile(true)}
                  className="px-6 py-2 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors"
                >
                  View Conversations
                </button>
              </div>
            </div>
          </>
        )}
        {activeConversationId && (
          <>
        <div className="flex-shrink-0">
          <ChatHeader
            key={activeConversationId || "no-convo"}
            activeConversation={conversations[activeConversationId]}
            onDeleteConversationRequest={handleDeleteConversationRequest}
            userName={headerDetails?.name || ""}
            avatarUrl={headerDetails?.avatar || ""}
            lastSeen={headerDetails?.status || ""}
            conversationType={headerDetails?.conversationType || ""}
            participantAvatars={headerDetails?.participantAvatars || []}
            isSelectModeActive={selectModeActive}
            selectedCount={selectedMessageIds.size}
            typingUsers={typingUsers}
            onCancelSelectMode={handleCancelSelectMode}
            onSelectAll={handleSelectAllMessages}
            currentUserId={currentUserId} // Pass current user ID
            onDeleteSelected={() => handleDeleteMessages(selectedMessageIds)}
            onManageGroupMembersClick={handleOpenGroupManagementModal}
            onCloseConversation={handleCloseConversation}
            onAvatarClick={() => console.log("Header Avatar Clicked")}
            onNameClick={() => console.log("Header Name Clicked")}
            onSearchTermChange={setMessageSearchTerm}
            onProfileInfoClick={() =>
              console.log("Header Profile Info Clicked")
            }
            onVoiceCallClick={() => console.log("Header Voice Call Clicked")}
            onSharedMediaClick={() =>
              console.log("Header Shared Media Clicked")
            }
            onBlockContactClick={() =>
              console.log("Header Block Contact Clicked")
            }
          />
        </div>

        <div className="flex-grow overflow-hidden relative">
          <ChatBody
            ref={chatBodyRef}
            key={activeConversationId}
            messages={processedMessages}
            pinnedMessage={processedPinnedMessage}
            selectModeActive={selectModeActive}
            selectedMessageIds={selectedMessageIds}
            onContextMenu={handleContextMenuCallback}
            onPinMessage={handlePinMessage}
            onToggleMessageSelection={handleToggleMessageSelection}
            isLoading={
              activeConversationId &&
              isLoadingMessages &&
              activeConversation?.messages?.length === 0
            }
          />
        </div>

        <MessageContextMenu
          visible={contextMenu.visible}
          x={contextMenu.x}
          y={contextMenu.y}
          messageId={contextMenu.messageId}
          pinnedMessageId={pinnedMessageIdFromConvo}
          onReply={handleReplyToMessage}
          onCopy={handleCopyMessage}
          onPin={handlePinMessage}
          onSelect={handleStartSelectFromMenu}
          onDelete={(messageId) => handleDeleteMessages([messageId])}
          id="message-context-menu"
        />

        <div className="flex-shrink-0">
          <MessageInput
            key={activeConversationId + "-input"}
            replyingToMessage={processedReplyPreview}
            onCancelReply={handleCancelReply}
            onSendMessage={handleSendMessage}
            onStartTyping={() => {
              if (conversationSubscriptionRef.current) {
                conversationSubscriptionRef.current.sendTypingStatus(true); // Notify backend: user started typing
              }
            }}
            onStopTyping={() => {
              if (conversationSubscriptionRef.current) {
                conversationSubscriptionRef.current.sendTypingStatus(false); // Notify backend: user stopped typing
              }
            }}
          />
        </div>
          </>
        )}
      </div>

      {activeConversation && activeConversation.type === 'group' && (
        <GroupManagementModal
          show={showGroupManagementModal}
          onClose={handleCloseGroupManagementModal}
          onOpen={handleOpenGroupManagementModal}
          activeConversation={activeConversation}
          currentUserId={currentUserId}
          isCurrentUserAdminOrCreator={isCurrentUserAdminOrCreator}
          onAddParticipant={handleAddParticipant}
          onRemoveParticipant={handleRemoveParticipant}
          onUpdateGroupInfo={updateGroupDetails}
        />
      )}
    </div>
  );
};

export default ChatInterface;
